// it is important to not calling the repository from the top level of the feature i.e `@features/user` or `@features/shared`
// why? because it will create a circular dependency, instead, we should call the repository from the nearest directory of the file
// i.e `data/repositories` or `domain/types` to avoid circular dependency

import { CatalogRepository } from "@features/catalog/data/repositories/catalog-repository";
import type {
  CatalogRepositoryContract,
  LockedProductsRepositoryContract,
} from "@features/catalog/domain";
import { CountriesRepository } from "@features/country/data";
import type { CountriesRepositoryContract } from "@features/country/domain";
import {
  FirebaseRemoteConfigRepository,
  UserFeaturesRepository,
} from "@features/feature-manager/data/repositories";
import type { FeaturesListRepositoryContract } from "@features/feature-manager/domain";
import { MerchantInsightsRepository } from "@features/insights/data";
import type { MerchantInsightsRepositoryContract } from "@features/insights/domain/types/insights-repository.contract";
import { NotificationsRepository } from "@features/notifications/data";
import type { NotificationsRepositoryContract } from "@features/notifications/domain";
import { OrdersRepository } from "@features/orders/data/repositories/orders.repository";
import type { OrdersRepositoryContract } from "@features/orders/domain";
import { ProductsRepository } from "@features/products/data";
import type { ProductsRepositoryContract } from "@features/products/domain";
import {
  AnnouncementsRepository,
  MarketClosureRepository,
  UtilsRepository,
} from "@features/shared/data";
import type {
  AnnouncementsRepositoryContract,
  MarketClosureRepositoryContract,
  UtilsRepositoryContract,
} from "@features/shared/domain";
import { getUserDevice } from "@features/shared/presentation";
import { DukanRepository, StoresRepository } from "@features/stores/data";
import type {
  DukanRepositoryContract,
  StoresRepositoryContract,
} from "@features/stores/domain";
import {
  AuthRepository,
  MerchantRepository,
  ProfileRepository,
  UserDevice,
  UTMDataRepository,
} from "@features/user/data";
import type {
  AuthRepositoryContract,
  MerchantRepositoryContract,
  UserProfileRepositoryContract,
  UTMDataRepositoryContract,
} from "@features/user/domain";
import { WalletRepository } from "@features/wallet/data/repositories/wallet.repository";
import type { WalletRepositoryContract } from "@features/wallet/domain";
import { KYCRepository } from "../features/kyc/data";
import type { KYCRepositoryContract } from "../features/kyc/domain";
import { PreOrderRepository } from "../features/pre-order/data";
import type { PreOrderRepositoryContract } from "../features/pre-order/domain";
import { singleton } from "./utils";

// Keep this line at the bottom of the imports to prevent circular dependency
import { CartRepository } from "@features/cart/data/repositories/cart-repository";
import type { CartRepositoryContract } from "@features/cart/domain";
import { LockedProductsRepository } from "@features/catalog/data/repositories/locked-products-repository";
import { CategoriesRepository } from "@features/categories/data";
import type { CategoriesRepositoryContract } from "@features/categories/domain";
import { CheckoutRepository } from "@features/checkout/data";
import type { CheckoutRepositoryContract } from "@features/checkout/domain";
import { DynamicIncentivesRepository } from "@features/dynamic-incentives/data";
import type { DynamicIncentivesRepositoryContract } from "@features/dynamic-incentives/domain";
import { LeadsRepository } from "@features/leads/data";
import type { LeadsRepositoryContract } from "@features/leads/domain";
import { LearningCenterRepository } from "@features/learning-center/data";
import { LearningCenterRepositoryContract } from "@features/learning-center/domain";
import { QuestionnaireRepository } from "@features/questionnaire/data";
import type { QuestionnaireRepositoryContract } from "@features/questionnaire/domain";
import { RecommendationsRepository } from "@features/recommendations/data";
import { RecommendationsRepositoryContract } from "@features/recommendations/domain";
import { ReferralProgramRepository } from "@features/referral-programs/data";
import type { ReferralProgramRepositoryContract } from "@features/referral-programs/domain";
import { TieringRepository } from "@features/tiering/data";
import type { TieringRepositoryContract } from "@features/tiering/domain";

// Application resolvers should be listed here like
export function resolveCountriesRepository(): CountriesRepositoryContract {
  return singleton("countries", () => new CountriesRepository());
}

/**
 * Resolve auth repository
 */
export function resolveAuthRepository(): AuthRepositoryContract {
  return singleton(
    "auth",
    () => new AuthRepository(resolveUTMDataRepository()),
  );
}

/**
 * Resolve user features repository
 */
export function resolveUserFeaturesRepository(): FeaturesListRepositoryContract {
  return new UserFeaturesRepository();
}

/**
 * Resolve firebase features repository
 */
export function resolveFirebaseRemoteConfigRepository(): FeaturesListRepositoryContract {
  return new FirebaseRemoteConfigRepository();
}

export function resolveKYCRepository(): KYCRepositoryContract {
  return new KYCRepository();
}

export function resolvePreOrderRepository(): PreOrderRepositoryContract {
  return new PreOrderRepository();
}

export function resolveMerchantRepository(): MerchantRepositoryContract {
  return new MerchantRepository();
}

export function resolveNotificationsRepository(): NotificationsRepositoryContract {
  return new NotificationsRepository();
}

export function resolveUserDevice(): UserDevice {
  return getUserDevice();
}

export function resolveProductsRepository(): ProductsRepositoryContract {
  return new ProductsRepository();
}

export function resolveProfileRepository(): UserProfileRepositoryContract {
  return new ProfileRepository();
}

export function resolveStoresRepository(): StoresRepositoryContract {
  return new StoresRepository();
}

export function resolveDukanRepository(): DukanRepositoryContract {
  return new DukanRepository();
}

export function resolveWalletRepository(): WalletRepositoryContract {
  return new WalletRepository();
}

export function resolveMarketClosureRepository(): MarketClosureRepositoryContract {
  return new MarketClosureRepository();
}

export function resolveInsightsRepository(): MerchantInsightsRepositoryContract {
  return new MerchantInsightsRepository();
}

export function resolveUtilsRepository(): UtilsRepositoryContract {
  return new UtilsRepository();
}

export function resolveOrderRepository(): OrdersRepositoryContract {
  return new OrdersRepository();
}

export function resolveLeadsRepository(): LeadsRepositoryContract {
  return new LeadsRepository();
}

export function resolveCatalogRepository(): CatalogRepositoryContract {
  return new CatalogRepository(
    resolveCheckoutRepository(),
    resolveProductsRepository(),
  );
}

export function resolveCartRepository(): CartRepositoryContract {
  return new CartRepository();
}

export function resolveCheckoutRepository(): CheckoutRepositoryContract {
  return new CheckoutRepository(resolveUTMDataRepository());
}

export function resolveQuestionnaireRepository(): QuestionnaireRepositoryContract {
  return new QuestionnaireRepository();
}

export function resolveUTMDataRepository(): UTMDataRepositoryContract {
  return new UTMDataRepository();
}

export function resolveLockedProductsRepository(): LockedProductsRepositoryContract {
  return new LockedProductsRepository();
}

export function resolveReferralProgramRepository(): ReferralProgramRepositoryContract {
  return new ReferralProgramRepository();
}

export function resolveAnnouncementsRepository(): AnnouncementsRepositoryContract {
  return new AnnouncementsRepository();
}

export function resolveCategoriesRepository(): CategoriesRepositoryContract {
  return new CategoriesRepository();
}

export function resolveTieringRepository(): TieringRepositoryContract {
  return new TieringRepository();
}

export function resolveDynamicIncentivesRepository(): DynamicIncentivesRepositoryContract {
  return new DynamicIncentivesRepository();
}

export function resolveRecommendationsRepository(): RecommendationsRepositoryContract {
  return new RecommendationsRepository();
}

export function resolveLearningCenterRepository(): LearningCenterRepositoryContract {
  return new LearningCenterRepository();
}
